import { Injectable } from '@angular/core';
import { IBarChart } from '../components/apache-bar-chart/bar-chart.model';

interface IRawChartData {
  [date: string]: { [key: string]: number | {[label: string]: number} };
}

@Injectable({
  providedIn: 'root',
})
export class ChartsUtilsService {

  /**
   * Maps `IRawChartData` to `IBarChart[]`. The function processes both simple and nested objects,
   * generating bar chart data with or without a stack value depending on the structure of the input.
   *
   * - If the input contains simple key-value pairs, it generates data without a stack.
   * - If the input contains nested objects, it generates data with a stack value corresponding to the outer key.
   *
   * Example:
   *
   * Simple object:
   * const obj = {
   *   "2024-02-02": { "product1": 5, "product2": 10 }
   * };
   * Result:
   * [
   *   {
   *     "date": "2024-02-02",
   *     "products": [
   *       { "label": "product1", "amount": 5 },
   *       { "label": "product2", "amount": 10 }
   *     ]
   *   }
   * ]
   *
   * Nested object:
   * const obj2 = {
   *   "2024-02-02": {
   *     "category1": { "product1": 5, "product2": 10 }
   *   }
   * };
   * Result:
   * [
   *   {
   *     "date": "2024-02-02",
   *     "products": [
   *       { "label": "product1", "amount": 5, "stack": "category1" },
   *       { "label": "product2", "amount": 10, "stack": "category1" }
   *     ]
   *   }
   * ]
   *
   * @param {IRawChartData} obj - The raw chart data to map. Can be a simple or nested object.
   * @param {boolean} [isDaily] - Optional flag to determine if the date should be formatted in a short format.
   * @returns {IBarChart[]} - The mapped bar chart data.
   */
  mapToBarChart(obj: IRawChartData, isDaily?: boolean): IBarChart[] {
    const mappedData = [];

    for (const [date, dataObj] of Object.entries(obj)) {
      const dataArr = [];

        for (const [key, value] of Object.entries(dataObj)) {
            if (typeof value === 'object') {
                for (const [label, amount] of Object.entries(value as { [label: string]: number })) {
                    dataArr.push({label: label, stack: key, amount: amount.toFixed(2)});
                }
                continue;
            }

            dataArr.push({label: key, amount: value.toFixed(2) });
        }

          mappedData.push({ date: isDaily ? this.mapDateToShortFormat(date) : date, products: dataArr });
      }

    return mappedData;
  }

  mapToCountriesChart(obj?): any {
    const keys = (obj.headers && obj.headers.length) ? obj.headers : Object.keys(obj.data);
    return keys.map(key => ({
      label: key,
      amount: obj.data[key],
    }));
  }

  renameKeysRecursive(obj: any, keyMap: Record<string, string>): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    const newObj: any = Array.isArray(obj) ? [] : {};

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];

        if (typeof value === 'object' && value !== null) {
          newObj[keyMap[key] || key] = this.renameKeysRecursive(value, keyMap);
        } else {
          newObj[keyMap[key] || key] = value;
        }
      }
    }

    return newObj;
  }

  mapDateToShortFormat(dateString) {
    const parts = dateString.split('-');
    return `${ parts[1] }-${ parts[2] }`;
  }

  convertKeysToFormattedDate(data: any): any {
    const [datePart, timePart] = data.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour] = timePart.split(' ').map(Number);

    return new Date(year, month - 1, day, hour)
      .toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
  }

  restructureChartObj(obj) {
    const newObj = {};
    for (const key in obj) {
      if (Object.hasOwnProperty.call(obj, key)) {
        const newKey = this.convertKeysToFormattedDate(key);
        newObj[newKey] = obj[key];
      }
    }
    return newObj;
  }

  formatNumber(value: number): string {
    if (Number.isInteger(value)) {
      return value.toString();
    } else {
      const roundedValue = value.toFixed(2);
      return roundedValue.endsWith('.00') ? roundedValue.slice(0, -3) + '00' : roundedValue;
    }
  }
}
